import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Mail, MessageSquare, ExternalLink, Info } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { DEFAULT_TEMPLATES, recommendTemplateKey, interpolate, resolveTemplate } from '../utils/messageTemplates';

/**
 * "Send as me" modal used inside View-as-Participant mode.
 *
 * It composes a personalised message using one of three built-in templates
 * (Reminder / Thank-you / Follow-up) — optionally overridden by admin-saved
 * templates from /admin/settings — and opens the user's native email or SMS
 * app via mailto: / sms: links. It never sends anything through a third-party
 * service; the admin's own device does the sending, so no SendGrid/Twilio
 * keys are required.
 */
const SendAsMeModal = ({ isOpen, onClose, participant, signup, initialKey = null }) => {
  const [templateKey, setTemplateKey] = useState(initialKey || recommendTemplateKey(signup));
  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [savedTemplates, setSavedTemplates] = useState({});
  const [edited, setEdited] = useState(false);

  const ctx = useMemo(() => ({
    participant_name: participant?.name || '',
    opportunity_title: signup?.opportunity_title || '',
    opportunity_date: signup?.opportunity_date || signup?.service_date || '',
    opportunity_location: signup?.opportunity_location || '',
    start_time: signup?.opportunity_start_time || signup?.start_time || '',
    end_time: signup?.opportunity_end_time || signup?.end_time || '',
    hours_credited: signup?.hours_credited || signup?.credit_hours || '',
  }), [participant, signup]);

  // Load saved templates from settings once per modal open
  useEffect(() => {
    if (!isOpen) return;
    setEdited(false);
    setTemplateKey(initialKey || recommendTemplateKey(signup));
    setChannel('email');
    axios.get(`${API}/admin/settings`)
      .then(res => setSavedTemplates(res.data || {}))
      .catch(() => setSavedTemplates({}));
  }, [isOpen, initialKey, signup]);

  // Re-interpolate when template key or context changes — unless the user has
  // manually edited the body (we preserve their tweaks).
  useEffect(() => {
    if (!isOpen) return;
    const tpl = resolveTemplate(templateKey, savedTemplates);
    if (!edited) {
      setSubject(interpolate(tpl.subject, ctx));
      setBody(interpolate(tpl.body, ctx));
    }
  }, [isOpen, templateKey, savedTemplates, ctx, edited]);

  const hasEmail = !!participant?.email;
  const hasPhone = !!participant?.phone;

  const openInApp = () => {
    if (!body.trim()) {
      toast.error('Message body is empty.');
      return;
    }
    try {
      if (channel === 'email') {
        if (!hasEmail) { toast.error('No email address on file for this participant.'); return; }
        const params = new URLSearchParams();
        if (subject) params.set('subject', subject);
        params.set('body', body);
        window.location.href = `mailto:${participant.email}?${params.toString()}`;
        toast.success('Opening in your email app…');
      } else {
        if (!hasPhone) { toast.error('No phone number on file for this participant.'); return; }
        window.location.href = `sms:${participant.phone}?body=${encodeURIComponent(body)}`;
        toast.success('Opening in your messaging app…');
      }
      onClose?.();
    } catch {
      toast.error('Unable to open native app — copy the message manually.');
    }
  };

  const resetTemplate = () => {
    const tpl = resolveTemplate(templateKey, savedTemplates);
    setSubject(interpolate(tpl.subject, ctx));
    setBody(interpolate(tpl.body, ctx));
    setEdited(false);
  };

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="send-as-me-modal">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-[#E9DDCF] flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-[#222]">Send a message</h2>
            <p className="text-xs text-[#666] mt-1">
              Opens your device's email or messaging app — sent from your account, no automation.
            </p>
          </div>
          <button onClick={onClose} className="text-[#666] hover:text-[#222]" data-testid="send-as-me-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-[#444]">
            To: <strong>{participant?.name || 'Participant'}</strong>{' '}
            <span className="text-[#888]">
              ({participant?.email || 'no email'}{participant?.phone ? ` · ${participant.phone}` : ''})
            </span>
          </div>

          {/* Template selector */}
          <div>
            <label className="block text-xs font-semibold text-[#666] mb-1.5">Template</label>
            <div className="flex flex-wrap gap-2" role="radiogroup">
              {Object.keys(DEFAULT_TEMPLATES).map(key => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setTemplateKey(key); setEdited(false); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                    templateKey === key
                      ? 'bg-[#8B2E3C] text-white border-[#8B2E3C]'
                      : 'bg-white text-[#444] border-[#E9DDCF] hover:border-[#8B2E3C]'
                  }`}
                  data-testid={`send-as-me-template-${key}`}
                >
                  {key === 'reminder' ? 'Reminder' : key === 'thankyou' ? 'Thank-you' : 'Follow-up'}
                </button>
              ))}
            </div>
          </div>

          {/* Channel selector */}
          <div>
            <label className="block text-xs font-semibold text-[#666] mb-1.5">Send via</label>
            <div className="flex gap-2" role="radiogroup">
              <button
                type="button"
                onClick={() => setChannel('email')}
                disabled={!hasEmail}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40 ${
                  channel === 'email'
                    ? 'bg-[#8B2E3C] text-white border-[#8B2E3C]'
                    : 'bg-white text-[#444] border-[#E9DDCF] hover:border-[#8B2E3C]'
                }`}
                data-testid="send-as-me-channel-email"
              >
                <Mail className="w-4 h-4" /> Email
              </button>
              <button
                type="button"
                onClick={() => setChannel('sms')}
                disabled={!hasPhone}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40 ${
                  channel === 'sms'
                    ? 'bg-[#8B2E3C] text-white border-[#8B2E3C]'
                    : 'bg-white text-[#444] border-[#E9DDCF] hover:border-[#8B2E3C]'
                }`}
                data-testid="send-as-me-channel-sms"
              >
                <MessageSquare className="w-4 h-4" /> Text
              </button>
            </div>
            {!hasEmail && !hasPhone && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><Info className="w-3 h-3" /> No email or phone on file for this participant.</p>
            )}
          </div>

          {channel === 'email' && (
            <div>
              <label className="block text-xs font-semibold text-[#666] mb-1.5">Subject</label>
              <input
                type="text"
                className="input-field"
                value={subject}
                onChange={(e) => { setSubject(e.target.value); setEdited(true); }}
                data-testid="send-as-me-subject"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-[#666] mb-1.5">Message</label>
            <textarea
              className="input-field"
              rows={channel === 'sms' ? 6 : 8}
              value={body}
              onChange={(e) => { setBody(e.target.value); setEdited(true); }}
              data-testid="send-as-me-body"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-[#888]">
                {channel === 'sms' ? `${body.length} characters` : 'HTML is not supported in native email app bodies.'}
              </p>
              {edited && (
                <button type="button" onClick={resetTemplate} className="text-xs text-[#8B2E3C] hover:underline" data-testid="send-as-me-reset">
                  Reset to template
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-[#E9DDCF] flex flex-wrap justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button
            onClick={openInApp}
            className="btn-primary flex items-center gap-2"
            disabled={(channel === 'email' && !hasEmail) || (channel === 'sms' && !hasPhone)}
            data-testid="send-as-me-open-button"
          >
            <ExternalLink className="w-4 h-4" />
            {channel === 'email' ? 'Open in Email App' : 'Open in Messages App'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendAsMeModal;
